<template>
	<div class="p-fluid p-formgrid p-grid p-input-filled">
		<div class="p-field p-col-6">
			<label for="interfaceName">{{$t('Squeeze.DocumentClasses.Type')}} *</label>
			<Dropdown
				id="interfaceName"
				style="min-width: 15.625rem"
				v-model="value.interfaceName"
				:options="allExportInterfaces"
				optionValue="id"
				optionLabel="name"
				:filter="true"
				:autoFilterFocus="true"
				:class="{'p-invalid':v$.interfaceName.$invalid && showErrorMessage}"
				@change="update"
				:disabled="!!value.id"
				@show="showDropdownOverlay(allExportInterfaces)"
			/>
			<small v-if="v$.interfaceName.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.DocumentClasses.Type')}) }}
			</small>
		</div>
		<div class="p-field p-col-6">
			<label for="description">{{$t('Squeeze.DocumentClasses.Description')}} *</label>
			<InputText id="description" v-model.trim="value.description" :class="{'p-invalid':v$.description.$invalid && showErrorMessage}" @change="update" required="true" />
			<small v-if="v$.description.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.DocumentClasses.Description')}) }}
			</small>
		</div>
		<template v-for="(property, index) in currentProperties" v-bind:key="property.id ?? index">
			<div class="p-field" :class="property.type === 'sequenceFromSet' ? 'p-col-12' : 'p-col-6'">
				<label :for="property.id">
					{{ property.name }}<template v-if="!property.optional"> *</template>
					<i
						v-if="property.id === 'SharePointGraphApiDrive'"
						v-tooltip="$t('Squeeze.DocumentClasses.SharepointGraphApiDriveFieldInfo')"
						class="mdi mdi-comment-question-outline"
						style="vertical-align: middle;"
					></i>
				</label>

				<InputText v-if="property.type === 'string'" :id="property.id" v-model.trim="value.configuration[property.id]" @change="update" />
				<InputNumber v-else-if="property.type === 'int'" :id="property.id" v-model="value.configuration[property.id]" @change="update" />
				<IconField v-else-if="property.type === 'password' && value.configuration[property.id]">
					<InputIcon
						v-if="value.configuration[property.id].isSet"
						class="mdi mdi-close"
						v-tooltip.bottom="$t('Squeeze.General.Delete')"
						style="cursor: pointer"
						@click="clearPassword(property)"
					/>
					<Password
						:id="property.id"
						v-model.trim="value.configuration[property.id].value"
						:placeholder="value.configuration[property.id].isSet ? '*****' : ''"
						@change="update"
						:feedback="false"
					/>
				</IconField>
				<Dropdown
					v-else-if="property.type === 'select' && property.id !== 'SharePointExportFolderStructure'"
					style="min-width: 15.625rem"
					v-model.trim="value.configuration[property.id]"
					:options="property.options"
					optionValue="id"
					optionLabel="name"
					@change="update"
					@show="showDropdownOverlay(property.options)"
				/>
				<Dropdown
					v-else-if="property.type === 'bool'"
					style="min-width: 15.625rem"
					v-model="value.configuration[property.id]"
					:options="listOfBooleans"
					optionValue="id"
					optionLabel="name"
					@change="update"
					@show="showDropdownOverlay(listOfBooleans)"
				/>
				<MultiSelect
					v-else-if="property.type === 'sequenceFromSet'"
					style="min-width: 15.625rem"
					v-model="value.configuration[property.id]"
					:options="property.options"
					optionValue="id"
					optionLabel="name"
					display="chip"
					:filter="true"
					:autoFilterFocus="true"
					@change="update"
					@show="showDropdownOverlay(property.options)"
				>
					<template #value="slotProps">
						<div v-tooltip.bottom="slotProps.value ? mapTooltipValues(slotProps.value) : ''">
							<template v-for="(option, optionIndex) in slotProps.value" v-bind:key="option">
								<Chip v-if="optionIndex <= 2" :label="option" removable class="p-mr-2" @remove="onRemovePropertyOption(property, option)" />
								<Chip v-else-if="optionIndex === 3" label="..." class="p-mr-2" />
							</template>
						</div>
					</template>
					<template #option="slotProps">
						{{ slotProps.option.name }}
					</template>
				</MultiSelect>
				<small v-if="showErrorMessage && isPropertyInvalid(property)" class="p-error">
					{{ $t('Forms.Val.MissingRequired', { field: property.name}) }}
				</small>
			</div>
		</template>
		<div class="p-field p-col-12">
			<label for="active">{{$t('Squeeze.DocumentClasses.Active')}}</label><br/>
			&nbsp;&nbsp;<Checkbox id="active" v-model="value.active" :binary="true" />
		</div>
	</div>
</template>

<script lang="ts">
import {computed, defineComponent, nextTick, onMounted, PropType, reactive, watch} from "vue";
import Checkbox from "primevue/checkbox";
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from "primevue/inputtext";
import Chip from 'primevue/chip';
import InputNumber from '@/components/DexInputNumber.vue';
import Dropdown from "primevue/dropdown";
import {
	ExportInterfaceDocumentationDto,
	ExportInterfaceDto,
	ExportInterfacePropertyDocumentationDto
} from "@dex/squeeze-client-ts";
import {useI18n} from "vue-i18n";
import {useVuelidate} from "@vuelidate/core";
import {helpers, required} from "@vuelidate/validators";
import Password from "primevue/password";
import MultiSelect from "primevue/multiselect";
import {showDropdownOverlay} from "@/util/StylesHelper";
import Tooltip from "primevue/tooltip";
import {FormState} from "@/util/Types";

type ExportInterfaceDtoFormState = FormState<ExportInterfaceDto, "interfaceName" | "description">;

interface PasswordField {
	value: string | null;
	isSet: boolean;
}

export default defineComponent({
	name: 'ExportInterfaceForm',
	components: {
		Checkbox,
		IconField,
		InputIcon,
		InputText,
		InputNumber,
		Dropdown,
		Password,
		MultiSelect,
		Chip,
	},
	props: {
		fieldEntry: {
			type: Object as PropType<ExportInterfaceDto>,
			default: () => ({}),
			required: true,
		},
		allExportInterfaces: {
			type: Array as PropType<ExportInterfaceDocumentationDto[]>,
			default: () => [],
			required: true,
		},
		showErrorMessage: {
			type: Boolean,
			default: false,
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: ['update'],
	setup(props, {emit}) {
		const {t} = useI18n();

		/** Normalizes configuration to always be a plain object */
		const normalizeConfig = (config: unknown) => Array.isArray(config) || !config ? {} : JSON.parse(JSON.stringify(config));

		/** Current Object of all input-fields */
		const value = reactive<ExportInterfaceDtoFormState>({
			...props.fieldEntry,
			// spelled out because a new interface carries neither of them
			interfaceName: props.fieldEntry.interfaceName,
			description: props.fieldEntry.description,
			configuration: normalizeConfig(props.fieldEntry.configuration),
		});

		/** Determines the required rules for validation */
		const rules = {
			interfaceName: { required },
			description: { required },
		}

		/** Use Vuelidate */
		const v$ = useVuelidate(rules, value);

		/** List of booleans */
		const listOfBooleans = computed(() => {
			return [
				{
					id: true,
					name: t('Squeeze.General.Yes'),
				},
				{
					id: false,
					name: t('Squeeze.General.No'),
				},
			]
		});

		/** Properties of the currently selected Export-Interface */
		const currentProperties = computed<ExportInterfacePropertyDocumentationDto[]>(() => {
			return props.allExportInterfaces.find(exportInterface => exportInterface.id === value.interfaceName)?.properties ?? [];
		});

		/**
		 * Brings a stored password into the PasswordField-shape. Older configurations kept a plain string, which the
		 * backend no longer returns, so such a value starts out empty.
		 * @param storedValue Value currently held in the configuration
		 */
		const toPasswordField = (storedValue: unknown): PasswordField => {
			if (!storedValue || typeof storedValue !== 'object') {
				return { value: null, isSet: false };
			}
			const {value: password, isSet} = storedValue as Partial<PasswordField>;
			return { value: password ?? null, isSet: isSet ?? !!password };
		}

		/** Normalizes every password-property of the selected Export-Interface */
		const normalizePasswordFields = () => {
			currentProperties.value
				.filter(property => property.type === 'password')
				.forEach(property => {
					value.configuration[property.id!] = toPasswordField(value.configuration[property.id!]);
				});
		}

		/** Normalize on creation, whenever another Export-Interface is selected */
		watch(currentProperties, normalizePasswordFields, {immediate: true});

		/**
		 * Checks if the Export-Interfaces Values are valid. Because of the generous structure, this can't be done
		 * with vuelidate
		 */
		const isPropertyInvalid = (property: ExportInterfacePropertyDocumentationDto): boolean => {
			if (property.optional) {
				return false;
			}
			const currentValue = value.configuration[property.id!];

			// A password is already valid when one is stored in the backend, even though no new one was entered
			if (property.type === 'password') {
				const password = currentValue as PasswordField | undefined;
				return !password?.isSet && !helpers.req(password?.value);
			}

			// Use Vuelidates default-required-check here
			return !helpers.req(currentValue);
		}

		/** Checks if any mandatory property of the selected Export-Interface is still missing a value */
		const hasInvalidProperties = (): boolean => currentProperties.value.some(isPropertyInvalid);

		/** Triggered on change of any field */
		const update = async () => {
			// need nextTick, because the dropdown don't check the change of a selected element (is too slow)
			await nextTick();
			emit("update", value, v$.value.$invalid || hasInvalidProperties());
		}

		onMounted(async () => {
			// Emit Validated-Values onMounted. Otherwise there can be validation-errors when editing multiple entries
			await v$.value.$validate();
			emit("update", value, v$.value.$invalid || hasInvalidProperties());
		});

		/** Removes the password stored in the backend */
		const clearPassword = (property: ExportInterfacePropertyDocumentationDto) => {
			value.configuration[property.id!] = { value: '', isSet: false };
			update();
		}

		/**
		 * Triggered when the tooltip is shown
		 * @param options Currently selected options
		 */
		const mapTooltipValues = (options: string[]) => {
			return options
				.map((option: string, index: number) => {
					return `${index + 1}: ${option}`;
				})
				.join("\n");
		}

		/**
		 * Triggered when clicked to remove a selected item
		 * @param property Property the option belongs to
		 * @param propertyOption Option to remove
		 */
		const onRemovePropertyOption = (property: ExportInterfacePropertyDocumentationDto, propertyOption: string) => {
			const selectedOptions: string[] = value.configuration[property.id!];
			const index = selectedOptions?.indexOf(propertyOption) ?? -1;
			if (index !== -1) {
				// remove selected option
				selectedOptions.splice(index, 1);
				update();
			}
		}

		return {
			v$,
			value,
			listOfBooleans,
			currentProperties,
			isPropertyInvalid,
			update,
			clearPassword,
			showDropdownOverlay,
			mapTooltipValues,
			onRemovePropertyOption,
		}
	},
});
</script>

<style scoped>

::v-deep(.p-multiselect-label-container) {
	padding: 0.42rem 0.42rem; /*height of multiselect field identical as to the other fields*/
}

::v-deep(.p-inputwrapper-filled.p-multiselect.p-multiselect-chip .p-multiselect-label) {
	padding: 0;
}

::v-deep(.p-chip .p-chip-text) {
	margin-top: 0.25rem;
	margin-bottom: 0.25rem;
}

</style>
